/**
 * AudioWorklet processor for microphone capture.
 *
 * Captures PCM16 audio at the configured sample rate and sends
 * base64-encoded chunks to the main thread via postMessage.
 *
 * Usage:
 *   const blob = new Blob([PcmCaptureProcessorCode], { type: 'application/javascript' });
 *   const url = URL.createObjectURL(blob);
 *   await audioContext.audioWorklet.addModule(url);
 *   const node = new AudioWorkletNode(audioContext, 'pcm-capture-processor');
 */

// This code runs inside the AudioWorklet scope.
// AudioWorkletGlobalScope does NOT have btoa/atob, so we send raw bytes
// via transferable ArrayBuffer and base64-encode on the main thread.
const PcmCaptureProcessorCode = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._buffer = [];
    this._bufferSize = 2400; // ~100ms at 24kHz
  }

  process(inputs) {
    const input = inputs[0];
    if (!input || !input[0]) return true;

    const samples = input[0];
    for (let i = 0; i < samples.length; i++) {
      // Convert float32 [-1, 1] to int16
      const s = Math.max(-1, Math.min(1, samples[i]));
      this._buffer.push(s < 0 ? s * 0x8000 : s * 0x7FFF);
    }

    if (this._buffer.length >= this._bufferSize) {
      const chunk = this._buffer.splice(0, this._bufferSize);
      const int16 = new Int16Array(chunk);
      // Transfer the buffer to the main thread (zero-copy)
      const bytes = new Uint8Array(int16.buffer.slice(0));
      this.port.postMessage({ type: 'audio', bytes: bytes.buffer }, [bytes.buffer]);
    }

    return true;
  }
}

registerProcessor('pcm-capture-processor', PcmCaptureProcessor);
`;

/**
 * Register the PCM capture AudioWorklet processor.
 *
 * @param audioContext - The AudioContext to register with.
 * @returns The URL of the worklet module (for cleanup).
 */
export async function registerCaptureProcessor(
  audioContext: AudioContext,
): Promise<string> {
  const blob = new Blob([PcmCaptureProcessorCode], {
    type: 'application/javascript',
  });
  const url = URL.createObjectURL(blob);
  await audioContext.audioWorklet.addModule(url);
  return url;
}

/**
 * Convert an ArrayBuffer to a base64 string.
 * Runs on the main thread where btoa is available.
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Create a capture AudioWorkletNode.
 *
 * @param audioContext - The AudioContext.
 * @param onChunk - Callback for each base64 PCM chunk.
 * @returns The AudioWorkletNode (connect your mic stream to this).
 */
export function createCaptureNode(
  audioContext: AudioContext,
  onChunk: (base64: string) => void,
): AudioWorkletNode {
  const node = new AudioWorkletNode(audioContext, 'pcm-capture-processor');
  node.port.onmessage = (event: MessageEvent) => {
    if (event.data?.type === 'audio' && event.data.bytes) {
      // Worklet sends raw ArrayBuffer; encode to base64 on the main thread
      const base64 = arrayBufferToBase64(event.data.bytes);
      onChunk(base64);
    }
  };
  return node;
}
